import { inject, Injectable } from "@angular/core";
import { ApiCurriculumCursus } from "@core/api/model/api-curriculum";
import { ApiCursus } from "@core/api/model/api-cursus";
import {
  ApiCursusDetail,
  ApiCursusPeriode,
  ApiOnderdeelVanMinor,
  ApiStudiejaar,
} from "@core/api/model/api-cursus-detail";
import { Course, PlanYearAndPeriod } from "@core/domain/course";
import { CourseDetail, CoursePeriod, CourseYear, PartOfMinor } from "@core/domain/course-detail";
import { CurriculumCourse } from "@core/domain/curriculum";
import { ApiHelper } from "@core/domain/helpers/api-helper";
import { CaciSanitizer } from "@core/util/caci-sanitizer";
import { Constants } from "../constants";

@Injectable({
  providedIn: "root",
})
export class CourseMappingService {
  private readonly caciSanitizer = inject(CaciSanitizer);

  private courseDetail!: CourseDetail;

  mapCourse(apiCursus: ApiCursus): Course {
    const enrolledPeriods = this.mapCourseEnrolledPeriods(apiCursus);
    return {
      id: apiCursus.spcu_id,
      code: apiCursus.cursus,
      name: apiCursus.korte_naam,
      startingBlock: apiCursus.aanvangsblok,
      timeslots: ApiHelper.stringListToArray(apiCursus.timeslots),
      customCourseName: apiCursus.eigen_cursus?.trim(),
      customCoursePoints: apiCursus.eigen_cursus_punten,
      studyProgram: apiCursus.examenprogramma,
      specialisationProgram: apiCursus.examenprogramma_specialisatie,
      minorProgram: apiCursus.examenprogramma_minor,
      examComponentCode: apiCursus.examenonderdeel || Constants.EXAM_COMPONENT_OTHER,
      examComponentName: apiCursus.examenonderdeel_naam,
      specialisationName: apiCursus.specialisatie_naam,
      minorName: apiCursus.minor_naam,
      isInPlanLater: ApiHelper.caciBooleanToBoolean(apiCursus.plan_later),
      statusObtained: ApiHelper.caciBooleanToBooleanOrUndefined(apiCursus.status_behaald),
      isEnrolled: enrolledPeriods.length > 0,
      enrolledPeriods: enrolledPeriods,
      ribbonPeriods: ApiHelper.numberListToArray(apiCursus.lint_periodes),
      replaceOld: apiCursus.vervanging_oud,
      replaceNew: apiCursus.vervanging_nieuw,
      labelColor: apiCursus.kleur,
      proposalStatus: apiCursus.status_goedgekeurd,
      studyPoints: apiCursus.studiepunten,
      isFlexCourse: ApiHelper.caciBooleanToBoolean(apiCursus.flex_cursus),
      isCourseAtAnotherInstitution: ApiHelper.caciBooleanToBoolean(apiCursus.bijvak_elders),
      isInRequiredExamComponent: ApiHelper.caciBooleanToBoolean(apiCursus.verplicht_examenonderdeel),
      statusMessages: [],

      categoryCode: apiCursus.categorie ?? "",
      categoryDescription: apiCursus.categorie_oms ?? "",

      // Transient fields:
      academicYear: 0,
      planId: 0,
      isRibbonRepeat: false,
      yearNr: 0, // If set, the course has been planned
      periodNr: 0,
      periodDescription: "",
      ribbonNr: 1,
    };
  }

  protected mapCourseEnrolledPeriods(apiCursus: ApiCursus | ApiCurriculumCursus): PlanYearAndPeriod[] {
    if (!ApiHelper.caciBooleanToBoolean(apiCursus.status_ingeschreven)) {
      return [];
    }
    // If enrolled, there is always a status study year and period
    return [
      {
        yearNr: apiCursus.status_studiejaar,
        periodNr: apiCursus.status_periode,
      },
    ];
  }

  mapCurriculumCourse(apiCursus: ApiCurriculumCursus): CurriculumCourse {
    const enrolledPeriods = this.mapCourseEnrolledPeriods(apiCursus);
    return {
      id: undefined, // does not exist for curriculum courses
      code: apiCursus.cursus,
      name: apiCursus.korte_naam,
      startingBlock: apiCursus.aanvangsblok,
      timeslots: ApiHelper.stringListToArray(apiCursus.timeslots),
      customCourseName: "", // TODO use other type without custom course?
      customCoursePoints: null, // TODO use other type without custom course?
      studyProgram: apiCursus.examenprogramma,
      examComponentCode: apiCursus.onderdeel,
      isInPlanLater: ApiHelper.caciBooleanToBoolean(apiCursus.plan_later),
      statusObtained: ApiHelper.caciBooleanToBooleanOrUndefined(apiCursus.status_behaald),
      isEnrolled: enrolledPeriods.length > 0,
      enrolledPeriods: enrolledPeriods,
      ribbonPeriods: ApiHelper.numberListToArray(apiCursus.lint_periodes),
      replaceOld: apiCursus.vervanging_oud,
      replaceNew: apiCursus.vervanging_nieuw,
      labelColor: "", // TODO apiCursus.kleur ??
      proposalStatus: apiCursus.status_goedgekeurd,
      studyPoints: apiCursus.studiepunten,
      isFlexCourse: false, // TODO ApiHelper.caciBooleanToBoolean(apiCursus.flex_cursus) ??
      isCourseAtAnotherInstitution: ApiHelper.caciBooleanToBoolean(apiCursus.bijvak_elders),
      statusMessages: [],
      categoryCode: "", // TODO apiCursus.categorie ??
      categoryDescription: apiCursus.categorie_omschrijving,

      // Transient fields:
      academicYear: 0,
      planId: 0,
      isRibbonRepeat: false,
      yearNr: 0, // If set, the course has been planned
      periodNr: 0,
      periodDescription: "",
      ribbonNr: 1,

      // From CurriculumCourse:
      kind: apiCursus.soort,
      examType: apiCursus.examentype,
      studyProgramCourse: apiCursus.examenprogramma_cursus,
      minor: apiCursus.minor,
      examComponentId: apiCursus.id_examenonderdeel,
      examComponentName: apiCursus.onderdeel_naam,
      studyProgramComponent: apiCursus.examenprogramma_onderdeel,
      typeDescription: apiCursus.type_omschrijving,
      unit: apiCursus.punteneenheid,
      progress: apiCursus.voortgang,
      enrolled: ApiHelper.caciBooleanToBoolean(apiCursus.ingeschreven),
      endResult: apiCursus.eindresultaat,
      resultDescription: apiCursus.resultaat_omschrijving,
      validityPeriodExpired: ApiHelper.caciBooleanToBoolean(apiCursus.geldigheidsduur_verlopen),
      adequate: ApiHelper.caciBooleanToBoolean(apiCursus.voldoende),
      testDate: apiCursus.toetsdatum,
      order: apiCursus.volgnummer,
      partOrder1: apiCursus.onderdeel_volgnummer_1,
      partOrder2: apiCursus.onderdeel_volgnummer_2,
      orderCourse: apiCursus.cursus_volgnummer,
      examComponentSPCU: apiCursus.examenonderdeel_spcu,
    };
  }

  mapCourseDetail(apiCursus: ApiCursusDetail): CourseDetail {
    if (apiCursus.statusmeldingen !== undefined) {
      this.courseDetail = {} as CourseDetail;
      return this.courseDetail;
    }

    this.courseDetail = {
      course: apiCursus.cursus,
      academicYear: apiCursus.collegejaar,
      name: apiCursus.korte_naam,
      category: apiCursus.categorie,
      categoryDescription: apiCursus.categorie_oms,
      maximumPoints: apiCursus.maximum_punten,
      minimumPoints: apiCursus.minimum_punten,
      pointsUnit: apiCursus.punteneenheid,
      studyPoints: apiCursus.studiepunten,
      insiderKnowledge: apiCursus.voorkennis,
      gainInsiderKnowledge: apiCursus.voorkennis_verkrijgen,
      selfstudy: apiCursus.zelfstudie,
      faculty: apiCursus.faculteit,
      facultyName: apiCursus.faculteit_naam,
      partOfMinor: apiCursus.onderdeel_van_minor?.map((minor) => this.toPartOfMinor(minor)),
      spcuId: apiCursus.spcu_id,
      isFlexCourse: ApiHelper.caciBooleanToBoolean(apiCursus.flex_cursus),
      hasOnlyTests: ApiHelper.caciBooleanToBoolean(apiCursus.alleen_toetsen),
      urlOwc: apiCursus.url_owc,
      tutors: ApiHelper.stringListToArray(apiCursus.docenten),
      mainLanguages: apiCursus.voertalen,
      prerequirement: this.caciSanitizer.sanitize(apiCursus.ingangseisen),
      prerequirementNotFulfilled: this.caciSanitizer.sanitize(apiCursus.ingangseisen_niet_voldaan),
      courseYears: apiCursus.studiejaren?.map((studiejaar) => this.toCourseYear(studiejaar)),
      content: this.caciSanitizer.sanitize(apiCursus.inhoud),
    };

    return this.courseDetail;
  }

  private toPartOfMinor(onderdeel: ApiOnderdeelVanMinor): PartOfMinor {
    return {
      studyProgram: onderdeel.examenprogramma,
      minorName: onderdeel.minor_naam,
      minor: onderdeel.minor,
    };
  }

  private toCourseYear(apiStudiejaar: ApiStudiejaar): CourseYear {
    return {
      academicYear: apiStudiejaar.collegejaar,
      flexiblePlanCourse: ApiHelper.caciBooleanToBoolean(apiStudiejaar.flexibel_plannen_cursus),
      yearNr: apiStudiejaar.studiejaar,
      academicYearDescription: apiStudiejaar.collegejaar_oms,
      periods: apiStudiejaar.periodes.map((periode) => this.toCoursePeriod(periode)),
    };
  }

  private toCoursePeriod(apiPeriode: ApiCursusPeriode): CoursePeriod {
    return {
      periodNr: apiPeriode.periode_nr,
      periodDescription: apiPeriode.periode_oms,
      ribbonPeriods: ApiHelper.numberListToArray(apiPeriode.lint_periodes),
      timeslots: ApiHelper.stringListToArray(apiPeriode.timeslots),
      offered: ApiHelper.caciBooleanToBoolean(apiPeriode.aangeboden),
    };
  }
}
